import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { ClipboardList, Edit, Save, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import mutate from "@/Utils/request/mutate";
import serviceRequestApi from "@/types/emr/serviceRequest/serviceRequestApi";

import { formatName } from "@/Utils/utils";
import { ActivityDefinitionReadSpec } from "@/types/emr/activityDefinition/activityDefinition";
import { ObservationDefinitionReadSpec } from "@/types/emr/observationDefinition/observationDefinition";
import {
  SERVICE_REQUEST_PRIORITY_COLORS,
  SERVICE_REQUEST_STATUS_COLORS,
  ServiceRequestReadSpec,
  toServiceRequestUpdateSpec,
} from "@/types/emr/serviceRequest/serviceRequest";
import { SpecimenDefinitionRead } from "@/types/emr/specimenDefinition/specimenDefinition";

function formatSpecimenRequirements(
  specimens: SpecimenDefinitionRead[],
): React.ReactNode {
  const counts = specimens.reduce(
    (acc: { [key: string]: number }, specimen) => {
      const type = specimen.type_collected?.display;
      if (type) {
        acc[type] = (acc[type] || 0) + 1;
      }
      return acc;
    },
    {},
  );

  return Object.entries(counts).map(
    ([type, count]: [string, number], index) => (
      <span key={type}>
        <span className="font-semibold">{type}</span>
        {count > 1 && <span> x {count}</span>}
        {index < Object.entries(counts).length - 1 && ", "}
      </span>
    ),
  );
}

interface ServiceRequestDetailsProps {
  request: ServiceRequestReadSpec;
  activityDefinition: ActivityDefinitionReadSpec;
  facilityId: string;
  disableEdit?: boolean;
  showDisabledEditButton?: boolean;
}

export function ServiceRequestDetails({
  request,
  activityDefinition,
  facilityId,
  disableEdit,
  showDisabledEditButton = false,
}: ServiceRequestDetailsProps) {
  const specimenRequirements = activityDefinition?.specimen_requirements ?? [];
  const observationRequirements =
    activityDefinition?.observation_result_requirements ?? [];

  const queryClient = useQueryClient();
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [patientInstruction, setPatientInstruction] = useState(
    request.patient_instruction ?? "",
  );
  const [appointmentDate, setAppointmentDate] = useState(
    request.occurance
      ? new Date(request.occurance)
          .toLocaleString("sv")
          .replace(" ", "T")
          .slice(0, 16)
      : "",
  );

  useEffect(() => {
    setPatientInstruction(request.patient_instruction ?? "");
    setAppointmentDate(
      request.occurance
        ? new Date(request.occurance)
            .toLocaleString("sv")
            .replace(" ", "T")
            .slice(0, 16)
        : "",
    );
  }, [request.patient_instruction, request.occurance]);

  const { mutate: updateServiceRequest, isPending: isUpdating } = useMutation({
    mutationFn: mutate(serviceRequestApi.updateServiceRequest, {
      pathParams: { facilityId, serviceRequestId: request.id },
    }),
    onSuccess: () => {
      toast.success(t("service_request_updated"));
      queryClient.invalidateQueries({
        queryKey: ["serviceRequest", facilityId, request.id],
      });
      setIsEditingInstructions(false);
    },
    onError: () => {
      toast.error(t("service_request_update_failed"));
    },
  });

  const handleSaveInstructions = () => {
    const updateSpec = toServiceRequestUpdateSpec(request, {
      patient_instruction: patientInstruction,
      occurance: appointmentDate
        ? new Date(appointmentDate).toISOString()
        : null,
    });
    updateServiceRequest(updateSpec);
  };

  return (
    <div className="bg-gray-100 rounded-lg border border-gray-200">
      <div className="py-3 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <div className="font-semibold text-gray-600 text-xl flex items-center gap-2">
            <div className="h-8 w-1.5 bg-gray-400 rounded-r-sm" />
            <div className="flex items-center gap-2 text-gray-700">
              {activityDefinition.title}
            </div>
          </div>
          <div className="font-medium px-3">
            {t("request id")}: {request.id}
          </div>
        </div>
        <div className="flex gap-2 items-center mr-4">
          {request.do_not_perform && (
            <Badge variant="destructive">{t("do not perform")}</Badge>
          )}
          <div className="gap-2">
            <div className="text-sm text-gray-600 mb-1">{t("intent")}</div>
            <div className="flex gap-2 font-semibold">{t(request.intent)}</div>
          </div>
        </div>
      </div>

      <div className="p-4 m-3 mt-1 bg-white rounded-lg shadow-md">
        <div className="flex flex-col md:flex-row">
          <div className="flex flex-col gap-6 mb-4 min-w-[50%]">
            <div className="flex gap-4">
              <div className="gap-2">
                <div className="text-sm text-gray-600 mb-1">
                  {t("priority")}
                </div>
                <div className="flex gap-2">
                  <Badge
                    variant={SERVICE_REQUEST_PRIORITY_COLORS[request.priority]}
                  >
                    {t(request.priority)}
                  </Badge>
                </div>
              </div>
              <div className="gap-2">
                <div className="text-sm text-gray-600 mb-1">{t("status")}</div>
                <div className="flex gap-2">
                  <Badge
                    variant={SERVICE_REQUEST_STATUS_COLORS[request.status]}
                  >
                    {t(request.status)}
                  </Badge>
                </div>
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-600 mb-1">
                {t("observation_definitions")}
              </div>
              <div className="font-sm font-normal flex flex-wrap gap-1">
                {observationRequirements.map(
                  (test: ObservationDefinitionReadSpec) => (
                    <Badge key={test.id} variant="secondary">
                      {test.title}
                    </Badge>
                  ),
                )}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">{t("specimen")}</div>
              <div className="font-sm font-normal flex flex-wrap gap-1">
                {formatSpecimenRequirements(specimenRequirements)}
              </div>
            </div>
          </div>
          <div className="border-l border-gray-200 mx-4" />
          <div className="flex flex-col gap-6">
            {activityDefinition.healthcare_service && (
              <div>
                <div className="text-sm text-gray-600 mb-1">
                  {t("healthcare_service")}
                </div>
                <div className="font-semibold text-gray-700">
                  {activityDefinition.healthcare_service.name}
                </div>
              </div>
            )}
            {request.locations?.length > 0 && (
              <div>
                <div className="text-sm text-gray-600 mb-1">
                  {t("locations")}
                </div>
                <div className="text-sm font-normal flex flex-wrap gap-1">
                  {request.locations.map((location) => (
                    <Badge key={location.id} variant="secondary">
                      {location.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="text-sm text-gray-600 mb-1">
                {t("requested by")}
              </div>
              <div className="font-semibold text-gray-700">
                {request.requester && formatName(request.requester)}
              </div>
            </div>
          </div>
        </div>

        {request.note && (
          <div className="mt-4">
            <div className="text-sm text-gray-600 mb-1">{t("note")}:</div>
            <div className="text-sm text-gray-950">{request.note}</div>
          </div>
        )}

        {/* Patient Instructions & Appointment Date Section */}
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className="size-4 text-amber-700" />
              <span className="text-sm font-semibold text-amber-800">
                {t("patient_instructions")} & {t("appointment_details")}
              </span>
            </div>
            {(!disableEdit || showDisabledEditButton) &&
              !isEditingInstructions && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                  disabled={disableEdit}
                  onClick={() => setIsEditingInstructions(true)}
                >
                  <Edit className="size-3.5 mr-1" />
                  {t("edit")}
                </Button>
              )}
            {!disableEdit && isEditingInstructions && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8"
                  disabled={isUpdating}
                  onClick={() => setIsEditingInstructions(false)}
                >
                  <X className="size-3.5 mr-1" />
                  {t("cancel")}
                </Button>
                <Button
                  size="sm"
                  className="h-8 gap-1"
                  disabled={isUpdating}
                  onClick={handleSaveInstructions}
                >
                  <Save className="size-3.5" />
                  {isUpdating ? t("saving") : t("save")}
                </Button>
              </div>
            )}
          </div>

          {isEditingInstructions ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-amber-900">
                  {t("patient_instruction")}
                </Label>
                <Textarea
                  value={patientInstruction}
                  onChange={(e) => setPatientInstruction(e.target.value)}
                  placeholder={t("enter_patient_instructions")}
                  className="w-full bg-white border-amber-300"
                  disabled={isUpdating}
                  rows={4}
                />
              </div>
              <div className="space-y-2 max-w-sm">
                <Label className="text-amber-900">
                  {t("appointment_date_time")}
                </Label>
                <Input
                  type="datetime-local"
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                  disabled={isUpdating}
                  className="bg-white border-amber-300"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-amber-700/80 uppercase tracking-wide">
                  {t("patient_instruction")}
                </Label>
                {request.patient_instruction ? (
                  <p className="mt-1 text-sm text-amber-950 whitespace-pre-wrap">
                    {request.patient_instruction}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-amber-900/60 italic">
                    {t("no_instructions_provided")}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs text-amber-700/80 uppercase tracking-wide">
                  {t("appointment_date_time")}
                </Label>
                <p className="mt-1 text-sm text-amber-950 font-medium whitespace-pre-wrap">
                  {request.occurance
                    ? new Date(request.occurance)
                        .toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                        .replace(/\//g, "-")
                    : "—"}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
